import { useRef } from 'react';
import type { CSSProperties } from 'react';
import type { FlyerDocElement } from '@/types/flyerDoc';
import type { Handle, PctRect } from '@/lib/flyer-canvas';
import { moveRect, resizeRect } from '@/lib/flyer-canvas';
import { zSorted } from '@/lib/flyer-compose';

const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_POS: Record<Handle, CSSProperties> = {
  nw: { left: -4, top: -4, cursor: 'nwse-resize' }, n: { left: 'calc(50% - 4px)', top: -4, cursor: 'ns-resize' },
  ne: { right: -4, top: -4, cursor: 'nesw-resize' }, e: { right: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
  se: { right: -4, bottom: -4, cursor: 'nwse-resize' }, s: { left: 'calc(50% - 4px)', bottom: -4, cursor: 'ns-resize' },
  sw: { left: -4, bottom: -4, cursor: 'nesw-resize' }, w: { left: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
};

const elRect = (e: FlyerDocElement): PctRect => ({ x: e.x_percent, y: e.y_percent, w: e.width_percent, h: e.height_percent });

interface ComposerCanvasProps {
  width: number; height: number;
  backgroundUrl: string | null;
  elements: FlyerDocElement[];
  selectedId: string | null;
  editingId: string | null;
  onSelect: (id: string | null) => void;
  onChangeRect: (id: string, r: PctRect) => void;
  onStartEdit: (id: string) => void;
  onCommitText: (id: string, text: string) => void;
}

export function ComposerCanvas({ width, height, backgroundUrl, elements, selectedId, editingId, onSelect, onChangeRect, onStartEdit, onCommitText }: ComposerCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: string; handle: Handle | 'move'; startX: number; startY: number; startRect: PctRect } | null>(null);

  const beginDrag = (e: React.PointerEvent, el: FlyerDocElement, handle: Handle | 'move') => {
    if (editingId === el.id) return; // typing, don't drag
    e.stopPropagation(); e.preventDefault();
    onSelect(el.id);
    drag.current = { id: el.id, handle, startX: e.clientX, startY: e.clientY, startRect: elRect(el) };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current; const c = canvasRef.current;
    if (!d || !c) return;
    const dx = (e.clientX - d.startX) / c.clientWidth;
    const dy = (e.clientY - d.startY) / c.clientHeight;
    onChangeRect(d.id, d.handle === 'move' ? moveRect(d.startRect, dx, dy) : resizeRect(d.startRect, d.handle, dx, dy));
  };
  const endDrag = () => { drag.current = null; };

  return (
    <div ref={canvasRef} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
      onClick={() => onSelect(null)}
      style={{ position: 'relative', width: '100%', aspectRatio: `${width} / ${height}`, overflow: 'hidden',
        touchAction: 'none', background: '#fafafa', border: '1px solid #eee', containerType: 'inline-size' }}>
      {backgroundUrl && <img src={backgroundUrl} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill' }} />}
      {zSorted(elements).map((el) => {
        const sel = el.id === selectedId;
        // style.fontSize is px at document scale → cqw = 1% of container width.
        const fontSizeCqw = ((el.style?.fontSize ?? 48) / width) * 100;
        return (
          <div key={el.id}
            onPointerDown={(e) => beginDrag(e, el, 'move')}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => { e.stopPropagation(); if (el.kind === 'text') onStartEdit(el.id); }}
            style={{ position: 'absolute',
              left: `${el.x_percent * 100}%`, top: `${el.y_percent * 100}%`,
              width: `${el.width_percent * 100}%`, height: `${el.height_percent * 100}%`,
              outline: sel ? '2px solid #1677ff' : '1px dashed rgba(0,0,0,.25)',
              cursor: 'move', overflow: 'hidden' }}>
            {el.kind === 'image' && <img src={el.src_url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none' }} />}
            {el.kind === 'text' && editingId !== el.id && (
              <div style={{ width: '100%', height: '100%', pointerEvents: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: `${fontSizeCqw}cqw`, lineHeight: 1.2,
                color: el.style?.color ?? '#000',
                textAlign: (el.style?.alignment as 'left' | 'center' | 'right') ?? 'left',
                fontWeight: el.style?.fontWeight === 'bold' ? 700 : 400,
                fontStyle: el.style?.isItalic ? 'italic' : 'normal',
                letterSpacing: el.style?.letterSpacing ? `${el.style.letterSpacing}px` : undefined }}>
                {el.text}
              </div>
            )}
            {el.kind === 'text' && editingId === el.id && (
              <textarea autoFocus defaultValue={el.text}
                onPointerDown={(e) => e.stopPropagation()}
                onBlur={(e) => onCommitText(el.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') (e.target as HTMLTextAreaElement).blur(); e.stopPropagation(); }}
                style={{ width: '100%', height: '100%', resize: 'none', border: 'none', outline: 'none',
                  background: 'rgba(255,255,255,.85)', fontSize: `${fontSizeCqw}cqw`, lineHeight: 1.2 }} />
            )}
            {sel && editingId !== el.id && HANDLES.map((h) => (
              <div key={h} data-handle={h} onPointerDown={(e) => beginDrag(e, el, h)}
                style={{ position: 'absolute', width: 8, height: 8, background: '#1677ff', borderRadius: 2, ...HANDLE_POS[h] }} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
